import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../config/api';
import './TeacherDashboard.css';

const TeacherDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [newClassName, setNewClassName] = useState('');
  const [isCreatingClass, setIsCreatingClass] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    // Redirect to auth if not logged in or not a teacher
    if (!user || user.role !== 'teacher') {
      navigate('/auth');
      return;
    }

    // Load existing class data and dashboard statistics
    loadClasses();
    loadDashboardStats();
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClasses = async () => {
    try {
      const response = await apiCall('/classes');
      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const response = await apiCall('/progress/dashboard');
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
        
        // Extract students with their full profiles from dashboard data
        const allStudentsWithStats = [];
        if (data.classes) {
          data.classes.forEach(cls => {
            if (cls.students) {
              cls.students.forEach(student => {
                allStudentsWithStats.push({
                  ...student,
                  className: cls.className,
                  classId: cls.id
                });
              });
            }
          });
        }
        setStudents(allStudentsWithStats);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const createNewClass = async () => {
    if (!newClassName.trim()) return;
    
    setIsCreatingClass(true);
    try {
      const response = await apiCall('/classes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ className: newClassName.trim() }),
      });

      if (response.ok) {
        setNewClassName('');
        // Reload classes to get updated data
        loadClasses();
        // Remove the alert - class creation success will be shown by updated class list
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to create class:', response.status, errorData);
        alert(`Failed to create class: ${errorData.message || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error creating class:', error);
      alert('Error creating class. Please try again.');
    } finally {
      setIsCreatingClass(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Class code copied to clipboard!');
  };

  const getStudentStats = (student) => {
    return {
      gamesPlayed: student.gamesPlayed || 0,
      winRate: student.accuracy || 0,
      quizAccuracy: student.accuracy || 0,
      questionsAnswered: student.questionsAnswered || 0,
      correctAnswers: student.correctAnswers || 0,
      lastPlayed: student.updatedAt ? new Date(student.updatedAt).toLocaleDateString() : 'Never'
    };
  };

  // Removed unused getClassStats function - stats are calculated inline in JSX

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  // Removed unused helper functions - calculations done inline in JSX

  return (
    <div className="teacher-dashboard">
      <div className="dashboard-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Game
        </button>
        <h1>👨‍🏫 My Class Dashboard</h1>
        <p>Welcome, {user.firstName}! Manage your class and view student progress.</p>
      </div>

      <div className="dashboard-content">
        {/* Create New Class Section */}
        <div className="dashboard-card create-class-section">
          <h2>➕ Create New Class</h2>
          <div className="create-class-form">
            <input
              type="text"
              placeholder="Enter class name (e.g., Biology 101, Period 3)"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              className="class-name-input"
            />
            <button 
              className="create-class-btn" 
              onClick={createNewClass} 
              disabled={isCreatingClass || !newClassName.trim()}
            >
              {isCreatingClass ? 'Creating...' : 'Create Class'}
            </button>
          </div>
        </div>

        {/* My Classes Section */}
        <div className="dashboard-card my-classes-section">
          <h2>📚 My Classes ({classes.length})</h2>
          {classes.length > 0 ? (
            <div className="classes-grid">
              {classes.map(classItem => (
                <div 
                  key={classItem.id || classItem._id} 
                  className="class-card"
                  onClick={() => {
                    const classId = classItem.id || classItem._id;
                    if (classId) {
                      navigate(`/class/${classId}`);
                    }
                  }}
                >
                  <div className="class-card-header">
                    <h3>{classItem.className}</h3>
                    <span className="student-count">{classItem.students.length} students</span>
                  </div>
                  <div className="class-card-body">
                    <div className="class-code-mini">
                      <span>Code: {classItem.classCode}</span>
                      <button 
                        className="copy-mini-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(classItem.classCode);
                        }}
                      >
                        📋
                      </button>
                    </div>
                    <div className="class-stats-mini">
                      <span>Created: {formatDate(classItem.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-classes">
              <p>You haven't created any classes yet. Create your first class above!</p>
            </div>
          )}
        </div>

        {/* Students Section */}
        <div className="dashboard-card students-section">
          <h2>👥 Students ({students.length})</h2>
          {students.length > 0 ? (
            <div className="students-list">
              {students.map((student, index) => {
                const stats = getStudentStats(student);
                
                return (
                  <div key={index} className="student-card">
                    <div className="student-info">
                      <h3>{student.name || `${student.firstName} ${student.lastName}`}</h3>
                      <p className="student-email">{student.email || 'Email not available'}</p>
                      <p className="student-class">Class: {student.className || 'Unknown'}</p>
                    </div>
                    <div className="student-stats">
                      <div className="stat-item">
                        <span className="stat-label">Games Played</span>
                        <span className="stat-value">{stats.gamesPlayed}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Win Rate</span>
                        <span className="stat-value">{stats.winRate}%</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Quiz Accuracy</span>
                        <span className="stat-value">{stats.quizAccuracy}%</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Last Played</span>
                        <span className="stat-value">{formatDate(stats.lastPlayedDate)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-students">
              <p>No students enrolled yet. Students can join your classes using the class codes above.</p>
            </div>
          )}
        </div>

        {/* Overview Stats */}
        <div className="dashboard-card overview-stats">
          <h2>📊 Overview</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">{dashboardStats?.totalClasses || classes.length}</span>
              <span className="stat-label">Classes</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{dashboardStats?.totalStudents || students.length}</span>
              <span className="stat-label">Students</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {dashboardStats?.totalGamesPlayed || students.reduce((total, student) => {
                  const stats = getStudentStats(student);
                  return total + stats.gamesPlayed;
                }, 0)}
              </span>
              <span className="stat-label">Total Games</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {dashboardStats?.averageScore || (students.length > 0 ? Math.round(
                  students.reduce((total, student) => {
                    const stats = getStudentStats(student);
                    return total + parseFloat(stats.winRate);
                  }, 0) / students.length
                ) : 0)}%
              </span>
              <span className="stat-label">Avg Score</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {dashboardStats?.totalQuestionsAnswered > 0 ? 
                  Math.round((dashboardStats.totalCorrectAnswers / dashboardStats.totalQuestionsAnswered) * 100) :
                  (students.length > 0 ? Math.round(
                    students.reduce((total, student) => {
                      const stats = getStudentStats(student);
                      return total + parseFloat(stats.quizAccuracy);
                    }, 0) / students.length
                  ) : 0)
                }%
              </span>
              <span className="stat-label">Avg Quiz Accuracy</span>
            </div>
          </div>
        </div>

      {/* Class Statistics Overview */}
      {students.length > 0 && (
        <div className="dashboard-card class-stats-section">
          <h2>📊 Class Overview</h2>
          <div className="class-stats-grid">
            <div className="class-stat-item">
              <span className="class-stat-number">{students.length}</span>
              <span className="class-stat-label">Total Students</span>
            </div>
            <div className="class-stat-item">
              <span className="class-stat-number">
                {students.reduce((total, student) => {
                  const stats = getStudentStats(student);
                  return total + stats.gamesPlayed;
                }, 0)}
              </span>
              <span className="class-stat-label">Total Games Played</span>
            </div>
            <div className="class-stat-item">
              <span className="class-stat-number">
                {students.length > 0 ? Math.round(
                  students.reduce((total, student) => {
                    const stats = getStudentStats(student);
                    return total + parseFloat(stats.winRate);
                  }, 0) / students.length
                ) : 0}%
              </span>
              <span className="class-stat-label">Average Win Rate</span>
            </div>
            <div className="class-stat-item">
              <span className="class-stat-number">
                {students.length > 0 ? Math.round(
                  students.reduce((total, student) => {
                    const stats = getStudentStats(student);
                    return total + parseFloat(stats.quizAccuracy);
                  }, 0) / students.length
                ) : 0}%
              </span>
              <span className="class-stat-label">Average Quiz Accuracy</span>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default TeacherDashboard;
